import { FilterType } from 'src/context/reducers/filterSetTypes';
import type { SiteOfCareType } from 'src/api/redux/suggestions/suggestionsApi';
import { countSocSelections, mergeSocRowsToOptions } from './utils';

export const SITE_OF_CARE_DISPLAY_TYPE = 'Site of Care';

export const isSocFilterType = (type: string): boolean =>
  type === FilterType.SOC_TYPE || type === FilterType.SOC_SUBTYPE;

/**
 * Filter types from the legacy Place of Service taxonomy that should be hidden from
 * previews so they match what actually gets queried. Site of Care is now the only active
 * taxonomy, so Place of Service is always suppressed. (Legacy saved filter sets from the
 * backend may still carry PLACE_OF_SERVICE rows — see the matching strip in useFilterSets'
 * apiFilterFormat.)
 */
export const getSocSuppressedFilterTypes = (): FilterType[] => [
  FilterType.PLACE_OF_SERVICE,
];

export const isSocSuppressedFilterType = (type: string): boolean =>
  getSocSuppressedFilterTypes().includes(type as FilterType);

export const extractRawValueKeys = (
  values: unknown[] | undefined,
  detailedValues: Array<{ value: string; label?: string }> | undefined
): string[] => {
  if (detailedValues && detailedValues.length > 0) {
    return detailedValues.map((dv) => String(dv?.value ?? '')).filter(Boolean);
  }
  if (!values) return [];
  return values
    .map((v) => {
      if (typeof v === 'string') return v;
      const obj = v as { value?: string | number };
      return obj?.value != null ? String(obj.value) : '';
    })
    .filter(Boolean);
};

type FilterItem = {
  type: string;
  values?: unknown[];
  detailed_values?: Array<{ value: string; label?: string }>;
};

/**
 * Pulls the SOC_TYPE / SOC_SUBTYPE values out of a filter-row list, leaving the
 * non-SOC rows behind.
 */
const partitionSocRows = (
  filterItems: FilterItem[]
): {
  socTypeValues: string[];
  socSubtypeValues: string[];
  otherRows: FilterItem[];
} => {
  const socTypeValues: string[] = [];
  const socSubtypeValues: string[] = [];
  const otherRows: FilterItem[] = [];

  (filterItems ?? []).forEach((item) => {
    if (!isSocFilterType(item?.type)) {
      otherRows.push(item);
      return;
    }
    const raw = extractRawValueKeys(item.values, item.detailed_values);
    if (item.type === FilterType.SOC_TYPE) {
      socTypeValues.push(...raw);
    } else {
      socSubtypeValues.push(...raw);
    }
  });

  return { socTypeValues, socSubtypeValues, otherRows };
};

/**
 * Counts applied filters the way the UI groups them — one per filter, with
 * SOC_TYPE and SOC_SUBTYPE collapsed to a single "Site of Care" entry.
 *
 * Counting raw rows makes one "Hospital > Childrens" selection read as 2, since
 * a single picker emits both rows.
 */
export const countAppliedFilterTypes = (filterItems: FilterItem[]): number => {
  const { socTypeValues, socSubtypeValues, otherRows } =
    partitionSocRows(filterItems);

  const otherCount = otherRows.filter(
    (item) =>
      extractRawValueKeys(item?.values, item?.detailed_values).length > 0
  ).length;

  const hasSoc = socTypeValues.length > 0 || socSubtypeValues.length > 0;

  return otherCount + (hasSoc ? 1 : 0);
};

/**
 * Counts individual selected values across applied filters, with the SOC pair
 * collapsed to the number of rows its picker actually shows (see
 * `countSocSelections`).
 */
export const countAppliedFilterValues = (filterItems: FilterItem[]): number => {
  const { socTypeValues, socSubtypeValues, otherRows } =
    partitionSocRows(filterItems);

  const otherCount = otherRows.reduce(
    (total, item) =>
      total + extractRawValueKeys(item?.values, item?.detailed_values).length,
    0
  );

  return otherCount + countSocSelections(socTypeValues, socSubtypeValues);
};

export const collectSiteOfCareLabels = (
  filterItems: Array<{
    type: string;
    values?: unknown[];
    detailed_values?: Array<{ value: string; label?: string }>;
  }>,
  socOptions: SiteOfCareType[]
): string[] => {
  const socTypeValues: string[] = [];
  const socSubtypeValues: string[] = [];

  filterItems.forEach((item) => {
    if (!isSocFilterType(item.type)) return;
    const raw = extractRawValueKeys(item.values, item.detailed_values);
    if (item.type === FilterType.SOC_TYPE) {
      socTypeValues.push(...raw);
    } else {
      socSubtypeValues.push(...raw);
    }
  });

  if (socTypeValues.length === 0 && socSubtypeValues.length === 0) return [];
  return mergeSocRowsToOptions(socTypeValues, socSubtypeValues, socOptions).map(
    (opt) => opt.label
  );
};
